import React, { useState, useEffect, useCallback } from 'react';
import 'flag-icons/css/flag-icons.min.css';
import Sidebar from '../components/Sidebar';
import { adminFetchInit } from '../utils/adminToken';

const Customer = () => {
  // State management
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  
  // Sidebar state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSidebarItem] = useState('customers');

  // Customer form state - aligned with Shopify API structure
  const [customerForm, setCustomerForm] = useState({
    // Basic customer info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryCode: '',
    country: '',
    note: '', // Shopify uses 'note' not 'notes'
    tags: '',
    // Address fields (matches Shopify addresses structure)
    address: {
      firstName: '',
      lastName: '',
      company: '',
      address1: '',
      address2: '',
      city: '',
      province: '',
      country: '',
      zip: '',
      phone: '',
      countryCode: '', // e.g., 'CA', 'US', 'PH'
      provinceCode: '' // e.g., 'ON', 'KY', province abbreviation
    },
    // Marketing preferences (matches Shopify consent structure)
    emailMarketingConsent: 'not_subscribed', // 'subscribed' | 'not_subscribed'
    smsMarketingConsent: 'not_subscribed', // 'subscribed' | 'not_subscribed'
    // Tax details
    taxExempt: false,
    vatNumber: ''
  });

  // Helper function to get flag class using flag-icons
  const getFlagClass = (countryCode) => {
    if (!countryCode || countryCode.length !== 2) {
      return null;
    }
    return `fi fi-${countryCode.toLowerCase()}`;
  };

  // Comprehensive country codes data with flag-icons integration
  const countryCodes = [
    { code: '+93', country: 'Afghanistan', countryCode: 'AF' },
    { code: '+355', country: 'Albania', countryCode: 'AL' },
    { code: '+213', country: 'Algeria', countryCode: 'DZ' },
    { code: '+1', country: 'American Samoa', countryCode: 'AS' },
    { code: '+376', country: 'Andorra', countryCode: 'AD' },
    { code: '+244', country: 'Angola', countryCode: 'AO' },
    { code: '+1', country: 'Anguilla', countryCode: 'AI' },
    { code: '+672', country: 'Antarctica', countryCode: 'AQ' },
    { code: '+1', country: 'Antigua and Barbuda', countryCode: 'AG' },
    { code: '+54', country: 'Argentina', countryCode: 'AR' },
    { code: '+374', country: 'Armenia', countryCode: 'AM' },
    { code: '+297', country: 'Aruba', countryCode: 'AW' },
    { code: '+61', country: 'Australia', countryCode: 'AU' },
    { code: '+43', country: 'Austria', countryCode: 'AT' },
    { code: '+994', country: 'Azerbaijan', countryCode: 'AZ' },
    { code: '+1', country: 'Bahamas', countryCode: 'BS' },
    { code: '+973', country: 'Bahrain', countryCode: 'BH' },
    { code: '+880', country: 'Bangladesh', countryCode: 'BD' },
    { code: '+1', country: 'Barbados', countryCode: 'BB' },
    { code: '+375', country: 'Belarus', countryCode: 'BY' },
    { code: '+32', country: 'Belgium', countryCode: 'BE' },
    { code: '+501', country: 'Belize', countryCode: 'BZ' },
    { code: '+229', country: 'Benin', countryCode: 'BJ' },
    { code: '+1', country: 'Bermuda', countryCode: 'BM' },
    { code: '+975', country: 'Bhutan', countryCode: 'BT' },
    { code: '+591', country: 'Bolivia', countryCode: 'BO' },
    { code: '+387', country: 'Bosnia and Herzegovina', countryCode: 'BA' },
    { code: '+267', country: 'Botswana', countryCode: 'BW' },
    { code: '+55', country: 'Brazil', countryCode: 'BR' },
    { code: '+246', country: 'British Indian Ocean Territory', countryCode: 'IO' },
    { code: '+1', country: 'British Virgin Islands', countryCode: 'VG' },
    { code: '+673', country: 'Brunei', countryCode: 'BN' },
    { code: '+359', country: 'Bulgaria', countryCode: 'BG' },
    { code: '+226', country: 'Burkina Faso', countryCode: 'BF' },
    { code: '+257', country: 'Burundi', countryCode: 'BI' },
    { code: '+855', country: 'Cambodia', countryCode: 'KH' },
    { code: '+237', country: 'Cameroon', countryCode: 'CM' },
    { code: '+1', country: 'Canada', countryCode: 'CA' },
    { code: '+238', country: 'Cape Verde', countryCode: 'CV' },
    { code: '+1', country: 'Cayman Islands', countryCode: 'KY' },
    { code: '+236', country: 'Central African Republic', countryCode: 'CF' },
    { code: '+235', country: 'Chad', countryCode: 'TD' },
    { code: '+56', country: 'Chile', countryCode: 'CL' },
    { code: '+86', country: 'China', countryCode: 'CN' },
    { code: '+61', country: 'Christmas Island', countryCode: 'CX' },
    { code: '+61', country: 'Cocos Islands', countryCode: 'CC' },
    { code: '+57', country: 'Colombia', countryCode: 'CO' },
    { code: '+269', country: 'Comoros', countryCode: 'KM' },
    { code: '+682', country: 'Cook Islands', countryCode: 'CK' },
    { code: '+506', country: 'Costa Rica', countryCode: 'CR' },
    { code: '+385', country: 'Croatia', countryCode: 'HR' },
    { code: '+53', country: 'Cuba', countryCode: 'CU' },
    { code: '+599', country: 'Curacao', countryCode: 'CW' },
    { code: '+357', country: 'Cyprus', countryCode: 'CY' },
    { code: '+420', country: 'Czech Republic', countryCode: 'CZ' },
    { code: '+243', country: 'Democratic Republic of the Congo', countryCode: 'CD' },
    { code: '+45', country: 'Denmark', countryCode: 'DK' },
    { code: '+253', country: 'Djibouti', countryCode: 'DJ' },
    { code: '+1', country: 'Dominica', countryCode: 'DM' },
    { code: '+1', country: 'Dominican Republic', countryCode: 'DO' },
    { code: '+670', country: 'East Timor', countryCode: 'TL' },
    { code: '+593', country: 'Ecuador', countryCode: 'EC' },
    { code: '+20', country: 'Egypt', countryCode: 'EG' },
    { code: '+503', country: 'El Salvador', countryCode: 'SV' },
    { code: '+240', country: 'Equatorial Guinea', countryCode: 'GQ' },
    { code: '+291', country: 'Eritrea', countryCode: 'ER' },
    { code: '+372', country: 'Estonia', countryCode: 'EE' },
    { code: '+251', country: 'Ethiopia', countryCode: 'ET' },
    { code: '+500', country: 'Falkland Islands', countryCode: 'FK' },
    { code: '+298', country: 'Faroe Islands', countryCode: 'FO' },
    { code: '+679', country: 'Fiji', countryCode: 'FJ' },
    { code: '+358', country: 'Finland', countryCode: 'FI' },
    { code: '+33', country: 'France', countryCode: 'FR' },
    { code: '+689', country: 'French Polynesia', countryCode: 'PF' },
    { code: '+241', country: 'Gabon', countryCode: 'GA' },
    { code: '+220', country: 'Gambia', countryCode: 'GM' },
    { code: '+995', country: 'Georgia', countryCode: 'GE' },
    { code: '+49', country: 'Germany', countryCode: 'DE' },
    { code: '+233', country: 'Ghana', countryCode: 'GH' },
    { code: '+350', country: 'Gibraltar', countryCode: 'GI' },
    { code: '+30', country: 'Greece', countryCode: 'GR' },
    { code: '+299', country: 'Greenland', countryCode: 'GL' },
    { code: '+1', country: 'Grenada', countryCode: 'GD' },
    { code: '+1', country: 'Guam', countryCode: 'GU' },
    { code: '+502', country: 'Guatemala', countryCode: 'GT' },
    { code: '+44', country: 'Guernsey', countryCode: 'GG' },
    { code: '+224', country: 'Guinea', countryCode: 'GN' },
    { code: '+245', country: 'Guinea-Bissau', countryCode: 'GW' },
    { code: '+592', country: 'Guyana', countryCode: 'GY' },
    { code: '+509', country: 'Haiti', countryCode: 'HT' },
    { code: '+504', country: 'Honduras', countryCode: 'HN' },
    { code: '+852', country: 'Hong Kong', countryCode: 'HK' },
    { code: '+36', country: 'Hungary', countryCode: 'HU' },
    { code: '+354', country: 'Iceland', countryCode: 'IS' },
    { code: '+91', country: 'India', countryCode: 'IN' },
    { code: '+62', country: 'Indonesia', countryCode: 'ID' },
    { code: '+98', country: 'Iran', countryCode: 'IR' },
    { code: '+964', country: 'Iraq', countryCode: 'IQ' },
    { code: '+353', country: 'Ireland', countryCode: 'IE' },
    { code: '+44', country: 'Isle of Man', countryCode: 'IM' },
    { code: '+972', country: 'Israel', countryCode: 'IL' },
    { code: '+39', country: 'Italy', countryCode: 'IT' },
    { code: '+225', country: 'Ivory Coast', countryCode: 'CI' },
    { code: '+1', country: 'Jamaica', countryCode: 'JM' },
    { code: '+81', country: 'Japan', countryCode: 'JP' },
    { code: '+44', country: 'Jersey', countryCode: 'JE' },
    { code: '+962', country: 'Jordan', countryCode: 'JO' },
    { code: '+7', country: 'Kazakhstan', countryCode: 'KZ' },
    { code: '+254', country: 'Kenya', countryCode: 'KE' },
    { code: '+686', country: 'Kiribati', countryCode: 'KI' },
    { code: '+383', country: 'Kosovo', countryCode: 'XK' },
    { code: '+965', country: 'Kuwait', countryCode: 'KW' },
    { code: '+996', country: 'Kyrgyzstan', countryCode: 'KG' },
    { code: '+856', country: 'Laos', countryCode: 'LA' },
    { code: '+371', country: 'Latvia', countryCode: 'LV' },
    { code: '+961', country: 'Lebanon', countryCode: 'LB' },
    { code: '+266', country: 'Lesotho', countryCode: 'LS' },
    { code: '+231', country: 'Liberia', countryCode: 'LR' },
    { code: '+218', country: 'Libya', countryCode: 'LY' },
    { code: '+423', country: 'Liechtenstein', countryCode: 'LI' },
    { code: '+370', country: 'Lithuania', countryCode: 'LT' },
    { code: '+352', country: 'Luxembourg', countryCode: 'LU' },
    { code: '+853', country: 'Macau', countryCode: 'MO' },
    { code: '+389', country: 'Macedonia', countryCode: 'MK' },
    { code: '+261', country: 'Madagascar', countryCode: 'MG' },
    { code: '+265', country: 'Malawi', countryCode: 'MW' },
    { code: '+60', country: 'Malaysia', countryCode: 'MY' },
    { code: '+960', country: 'Maldives', countryCode: 'MV' },
    { code: '+223', country: 'Mali', countryCode: 'ML' },
    { code: '+356', country: 'Malta', countryCode: 'MT' },
    { code: '+692', country: 'Marshall Islands', countryCode: 'MH' },
    { code: '+222', country: 'Mauritania', countryCode: 'MR' },
    { code: '+230', country: 'Mauritius', countryCode: 'MU' },
    { code: '+262', country: 'Mayotte', countryCode: 'YT' },
    { code: '+52', country: 'Mexico', countryCode: 'MX' },
    { code: '+691', country: 'Micronesia', countryCode: 'FM' },
    { code: '+373', country: 'Moldova', countryCode: 'MD' },
    { code: '+377', country: 'Monaco', countryCode: 'MC' },
    { code: '+976', country: 'Mongolia', countryCode: 'MN' },
    { code: '+382', country: 'Montenegro', countryCode: 'ME' },
    { code: '+1', country: 'Montserrat', countryCode: 'MS' },
    { code: '+212', country: 'Morocco', countryCode: 'MA' },
    { code: '+258', country: 'Mozambique', countryCode: 'MZ' },
    { code: '+95', country: 'Myanmar', countryCode: 'MM' },
    { code: '+264', country: 'Namibia', countryCode: 'NA' },
    { code: '+674', country: 'Nauru', countryCode: 'NR' },
    { code: '+977', country: 'Nepal', countryCode: 'NP' },
    { code: '+31', country: 'Netherlands', countryCode: 'NL' },
    { code: '+599', country: 'Netherlands Antilles', countryCode: 'AN' },
    { code: '+687', country: 'New Caledonia', countryCode: 'NC' },
    { code: '+64', country: 'New Zealand', countryCode: 'NZ' },
    { code: '+505', country: 'Nicaragua', countryCode: 'NI' },
    { code: '+227', country: 'Niger', countryCode: 'NE' },
    { code: '+234', country: 'Nigeria', countryCode: 'NG' },
    { code: '+683', country: 'Niue', countryCode: 'NU' },
    { code: '+850', country: 'North Korea', countryCode: 'KP' },
    { code: '+1', country: 'Northern Mariana Islands', countryCode: 'MP' },
    { code: '+47', country: 'Norway', countryCode: 'NO' },
    { code: '+968', country: 'Oman', countryCode: 'OM' },
    { code: '+92', country: 'Pakistan', countryCode: 'PK' },
    { code: '+680', country: 'Palau', countryCode: 'PW' },
    { code: '+970', country: 'Palestine', countryCode: 'PS' },
    { code: '+507', country: 'Panama', countryCode: 'PA' },
    { code: '+675', country: 'Papua New Guinea', countryCode: 'PG' },
    { code: '+595', country: 'Paraguay', countryCode: 'PY' },
    { code: '+51', country: 'Peru', countryCode: 'PE' },
    { code: '+63', country: 'Philippines', countryCode: 'PH' },
    { code: '+64', country: 'Pitcairn', countryCode: 'PN' },
    { code: '+48', country: 'Poland', countryCode: 'PL' },
    { code: '+351', country: 'Portugal', countryCode: 'PT' },
    { code: '+1', country: 'Puerto Rico', countryCode: 'PR' },
    { code: '+974', country: 'Qatar', countryCode: 'QA' },
    { code: '+242', country: 'Republic of the Congo', countryCode: 'CG' },
    { code: '+262', country: 'Reunion', countryCode: 'RE' },
    { code: '+40', country: 'Romania', countryCode: 'RO' },
    { code: '+7', country: 'Russia', countryCode: 'RU' },
    { code: '+250', country: 'Rwanda', countryCode: 'RW' },
    { code: '+590', country: 'Saint Barthelemy', countryCode: 'BL' },
    { code: '+290', country: 'Saint Helena', countryCode: 'SH' },
    { code: '+1', country: 'Saint Kitts and Nevis', countryCode: 'KN' },
    { code: '+1', country: 'Saint Lucia', countryCode: 'LC' },
    { code: '+590', country: 'Saint Martin', countryCode: 'MF' },
    { code: '+508', country: 'Saint Pierre and Miquelon', countryCode: 'PM' },
    { code: '+1', country: 'Saint Vincent and the Grenadines', countryCode: 'VC' },
    { code: '+685', country: 'Samoa', countryCode: 'WS' },
    { code: '+378', country: 'San Marino', countryCode: 'SM' },
    { code: '+239', country: 'Sao Tome and Principe', countryCode: 'ST' },
    { code: '+966', country: 'Saudi Arabia', countryCode: 'SA' },
    { code: '+221', country: 'Senegal', countryCode: 'SN' },
    { code: '+381', country: 'Serbia', countryCode: 'RS' },
    { code: '+248', country: 'Seychelles', countryCode: 'SC' },
    { code: '+232', country: 'Sierra Leone', countryCode: 'SL' },
    { code: '+65', country: 'Singapore', countryCode: 'SG' },
    { code: '+1', country: 'Sint Maarten', countryCode: 'SX' },
    { code: '+421', country: 'Slovakia', countryCode: 'SK' },
    { code: '+386', country: 'Slovenia', countryCode: 'SI' },
    { code: '+677', country: 'Solomon Islands', countryCode: 'SB' },
    { code: '+252', country: 'Somalia', countryCode: 'SO' },
    { code: '+27', country: 'South Africa', countryCode: 'ZA' },
    { code: '+82', country: 'South Korea', countryCode: 'KR' },
    { code: '+211', country: 'South Sudan', countryCode: 'SS' },
    { code: '+34', country: 'Spain', countryCode: 'ES' },
    { code: '+94', country: 'Sri Lanka', countryCode: 'LK' },
    { code: '+249', country: 'Sudan', countryCode: 'SD' },
    { code: '+597', country: 'Suriname', countryCode: 'SR' },
    { code: '+47', country: 'Svalbard and Jan Mayen', countryCode: 'SJ' },
    { code: '+268', country: 'Swaziland', countryCode: 'SZ' },
    { code: '+46', country: 'Sweden', countryCode: 'SE' },
    { code: '+41', country: 'Switzerland', countryCode: 'CH' },
    { code: '+963', country: 'Syria', countryCode: 'SY' },
    { code: '+886', country: 'Taiwan', countryCode: 'TW' },
    { code: '+992', country: 'Tajikistan', countryCode: 'TJ' },
    { code: '+255', country: 'Tanzania', countryCode: 'TZ' },
    { code: '+66', country: 'Thailand', countryCode: 'TH' },
    { code: '+228', country: 'Togo', countryCode: 'TG' },
    { code: '+690', country: 'Tokelau', countryCode: 'TK' },
    { code: '+676', country: 'Tonga', countryCode: 'TO' },
    { code: '+1', country: 'Trinidad and Tobago', countryCode: 'TT' },
    { code: '+216', country: 'Tunisia', countryCode: 'TN' },
    { code: '+90', country: 'Turkey', countryCode: 'TR' },
    { code: '+993', country: 'Turkmenistan', countryCode: 'TM' },
    { code: '+1', country: 'Turks and Caicos Islands', countryCode: 'TC' },
    { code: '+688', country: 'Tuvalu', countryCode: 'TV' },
    { code: '+1', country: 'U.S. Virgin Islands', countryCode: 'VI' },
    { code: '+256', country: 'Uganda', countryCode: 'UG' },
    { code: '+380', country: 'Ukraine', countryCode: 'UA' },
    { code: '+971', country: 'United Arab Emirates', countryCode: 'AE' },
    { code: '+44', country: 'United Kingdom', countryCode: 'GB' },
    { code: '+1', country: 'United States', countryCode: 'US' },
    { code: '+598', country: 'Uruguay', countryCode: 'UY' },
    { code: '+998', country: 'Uzbekistan', countryCode: 'UZ' },
    { code: '+678', country: 'Vanuatu', countryCode: 'VU' },
    { code: '+379', country: 'Vatican', countryCode: 'VA' },
    { code: '+58', country: 'Venezuela', countryCode: 'VE' },
    { code: '+84', country: 'Vietnam', countryCode: 'VN' },
    { code: '+681', country: 'Wallis and Futuna', countryCode: 'WF' },
    { code: '+212', country: 'Western Sahara', countryCode: 'EH' },
    { code: '+967', country: 'Yemen', countryCode: 'YE' },
    { code: '+260', country: 'Zambia', countryCode: 'ZM' },
    { code: '+263', country: 'Zimbabwe', countryCode: 'ZW' }
  ];

  // Show toast notification
  const showToastMessage = useCallback((message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  }, []);

  // Fetch customers from Shopify API
  const fetchCustomers = useCallback(async (showToast = true) => {
    setLoadingCustomers(true);
    try {
      const response = await fetch('/.netlify/functions/shopify-get-customers', adminFetchInit({ method: 'GET' }));

      const data = await response.json();

      if (data.success) {
        setCustomers(data.customers || []);
        
        // Only show toast on initial load, not on refresh after creating customer
        if (showToast) {
          showToastMessage(`Loaded ${data.customers?.length || 0} customers from Shopify`, 'success');
        }
        
        console.log('✅ Real Shopify customers loaded:', data.customers);
        
        // Debug customer name data with more detail
        data.customers?.forEach((customer, index) => {
          console.log(`🔍 Customer ${index + 1} (ID: ${customer.id}):`, {
            firstName: customer.firstName,
            lastName: customer.lastName,
            email: customer.email,
            emailSubscription: customer.emailSubscription,
            fullName: `${customer.firstName} ${customer.lastName}`.trim(),
            hasFirstName: !!customer.firstName,
            hasLastName: !!customer.lastName,
            displayName: customer.firstName || customer.lastName 
              ? `${customer.firstName} ${customer.lastName}`.trim()
              : customer.email?.split('@')[0] || 'Unnamed Customer'
          });
        });
      } else {
        console.error('❌ Shopify API failed:', data.error);
        if (showToast) {
          showToastMessage(`Shopify API Error: ${data.error}`, 'error');
          
          // Show specific error debugging information
          if (typeof data.error === 'string' && data.error.includes('configuration')) {
            showToastMessage('Check SHOPIFY_SHOP_NAME and SHOPIFY_ACCESS_TOKEN environment variables', 'error');
          }
        }
        
        // Only show empty list instead of fallback sample data
        setCustomers([]);
      }
    } catch (error) {
      console.error('❌ Network error fetching customers:', error);
      if (showToast) {
        showToastMessage(`Network Error: ${error.message}`, 'error');
      }
      
      // Only show empty list instead of fallback sample data
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  }, [showToastMessage]);

  // Load customers on component mount
  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Filter customers based on search
  const filteredCustomers = customers.filter(customer => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.toLowerCase();
    return fullName.includes(searchLower) || 
           (customer.email || '').toLowerCase().includes(searchLower) ||
           (customer.location || '').toLowerCase().includes(searchLower);
  });

  // Handle form changes (supports nested address fields)
  const handleFormChange = (field, value) => {
    if (field.startsWith('address.')) {
      const addressField = field.replace('address.', '');
      setCustomerForm(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setCustomerForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Handle country code change and auto-set address country
  const handleCountryCodeChange = (code) => {
    const country = countryCodes.find(c => c.code === code);
    setCustomerForm(prev => ({
      ...prev,
      countryCode: code,
      country: country?.country || '',
      address: {
        ...prev.address,
        country: country?.country || '',
        countryCode: country?.countryCode || '',
        // Auto-fill address name fields if they're empty
        firstName: prev.address.firstName || prev.firstName,
        lastName: prev.address.lastName || prev.lastName,
        phone: prev.address.phone || prev.phone
      }
    }));
  };

  // Create customer
  const handleCreateCustomer = async () => {
    try {
      // Validate required fields
      if (!customerForm.firstName.trim() || !customerForm.email.trim()) {
        showToastMessage('Please fill in required fields (First name, Email)', 'error');
        return;
      }

      // Show loading state
      showToastMessage('Creating customer...', 'info');

      // Prepare customer data for Shopify API (aligned with API response structure)
      const customerData = {
        customer: {
          first_name: customerForm.firstName || null,
          last_name: customerForm.lastName || null,
          email: customerForm.email,
          phone: customerForm.phone && customerForm.countryCode ? 
                 customerForm.countryCode + customerForm.phone : null,
          verified_email: true,
          note: customerForm.note || null,
          tags: customerForm.tags || '',
          tax_exempt: customerForm.taxExempt || false,
          // Marketing consent structure matching Shopify API
          email_marketing_consent: {
            state: customerForm.emailMarketingConsent,
            opt_in_level: customerForm.emailMarketingConsent === 'subscribed' ? 'single_opt_in' : null
          },
          sms_marketing_consent: {
            state: customerForm.smsMarketingConsent,
            opt_in_level: customerForm.smsMarketingConsent === 'subscribed' ? 'single_opt_in' : null,
            consent_collected_from: 'OTHER'
          },
          // Address structure matching Shopify API
          addresses: customerForm.address.address1 ? [{
            first_name: customerForm.address.firstName || null,
            last_name: customerForm.address.lastName || null,
            company: customerForm.address.company || null,
            address1: customerForm.address.address1,
            address2: customerForm.address.address2 || null,
            city: customerForm.address.city || null,
            province: customerForm.address.province || null,
            country: customerForm.address.country || null,
            zip: customerForm.address.zip || null,
            phone: customerForm.address.phone || null,
            country_code: customerForm.address.countryCode || null,
            province_code: customerForm.address.provinceCode || null
          }] : [],
          send_email_welcome: false
        }
      };

      console.log('🚀 Creating customer with data:', JSON.stringify(customerData, null, 2));

      const response = await fetch('/.netlify/functions/shopify-create-customer', adminFetchInit({
        method: 'POST',
        body: JSON.stringify(customerData),
      }));

      const data = await response.json();
      console.log('📋 Shopify API Response:', data);

      if (data.success) {
        showToastMessage(`Customer "${customerForm.firstName} ${customerForm.lastName}" created successfully!`, 'success');
        
        // IMMEDIATELY add the customer to the local list for instant display
        const newCustomer = {
          id: data.customer?.id || Date.now(), // Use Shopify ID if available, fallback to timestamp
          firstName: customerForm.firstName || '',
          lastName: customerForm.lastName || '',
          email: customerForm.email || '',
          phone: customerForm.phone && customerForm.countryCode ? 
                 customerForm.countryCode + customerForm.phone : '',
          totalSpent: '€0.00',
          ordersCount: 0,
          location: customerForm.address.city && customerForm.address.country ? 
                   `${customerForm.address.city}, ${customerForm.address.country}` : 
                   (customerForm.address.country || 'Not provided'),
          emailSubscription: customerForm.emailMarketingConsent === 'subscribed' ? 'Subscribed' : 'Not subscribed',
          acceptsMarketing: customerForm.emailMarketingConsent === 'subscribed',
          acceptsSmsMarketing: customerForm.smsMarketingConsent === 'subscribed',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          tags: customerForm.tags || '',
          note: customerForm.note || '',
          addresses: customerForm.address.address1 ? [customerForm.address] : [],
          defaultAddress: customerForm.address.address1 ? customerForm.address : null
        };
        
        // Add to the beginning of the customers list for immediate display
        setCustomers(prevCustomers => [newCustomer, ...prevCustomers]);
        console.log('✅ Customer added to local list immediately');
        
        setShowCreateModal(false);
        resetForm();
      } else {
        console.error('❌ Failed to create customer:', data.error);
        
        // Extract exact error messages from Shopify API response
        let errorMessage = 'Failed to create customer';
        
        if (data.error) {
          // Check if it's a Shopify API error with specific field errors
          if (typeof data.error === 'object' && data.error.errors) {
            // Parse Shopify error structure: { errors: { email: [ 'has already been taken' ] } }
            const errors = data.error.errors;
            const errorMessages = [];
            
            for (const [field, messages] of Object.entries(errors)) {
              if (Array.isArray(messages)) {
                // Extract each error message for the field
                messages.forEach(msg => {
                  errorMessages.push(`${field}: ${msg}`);
                });
              }
            }
            
            if (errorMessages.length > 0) {
              errorMessage = errorMessages.join(', ');
            }
          } else if (typeof data.error === 'string') {
            // Simple string error message
            errorMessage = data.error;
          } else if (data.error.message) {
            // Error object with message property
            errorMessage = data.error.message;
          }
        }
        
        console.log('📋 Parsed error message:', errorMessage);
        showToastMessage(errorMessage, 'error');
      }
    } catch (error) {
      console.error('❌ Error creating customer:', error);
      showToastMessage(`Failed to create customer: ${error.message}`, 'error');
    }
  };

  // Reset form to empty/null defaults
  const resetForm = () => {
    setCustomerForm({
      // Basic customer info
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      countryCode: '',
      country: '',
      note: '',
      tags: '',
      // Address fields (matches Shopify addresses structure)
      address: {
        firstName: '',
        lastName: '',
        company: '',
        address1: '',
        address2: '',
        city: '',
        province: '',
        country: '',
        zip: '',
        phone: '',
        countryCode: '',
        provinceCode: ''
      },
      // Marketing preferences (matches Shopify consent structure)
      emailMarketingConsent: 'not_subscribed',
      smsMarketingConsent: 'not_subscribed',
      // Tax details
      taxExempt: false,
      vatNumber: ''
    });
  };

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="flex h-screen">
        {/* Sidebar */}
        <Sidebar 
          activeSidebarItem={activeSidebarItem}
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={closeMobileMenu}
          dashboardType="admin"
        />

        {/* Mobile Overlay */}
        {isMobileMenuOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={closeMobileMenu}
          />
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          {/* Header */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Mobile Menu Button */}
                <button
                  onClick={toggleMobileMenu}
                  className="lg:hidden text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                
                <div>
                  <h1 className="text-2xl font-bold text-white">Customers</h1>
                  <p className="text-gray-400 mt-1">Manage your customer database and relationships</p>
                </div>
              </div>
              <button 
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg transition-all duration-200 flex items-center space-x-2 shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>Add Customer</span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto sidebar-scroll p-6">
            {/* Search and Filters */}
            <div className="bg-gray-800 rounded-2xl border border-gray-700 p-6 mb-6">
              <div className="flex items-center space-x-4">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 pl-10 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                    placeholder="Search customers..."
                  />
                  <svg className="w-5 h-5 text-gray-400 absolute left-3 top-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <button
                  onClick={() => fetchCustomers(true)}
                  disabled={loadingCustomers}
                  className="px-4 py-3 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg text-gray-300 hover:text-white transition-colors disabled:opacity-50"
                  title="Refresh customers"
                >
                  <svg className={`w-5 h-5 ${loadingCustomers ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                <button
                  onClick={async () => {
                    console.log('🔍 Testing Shopify API directly...');
                    try {
                      const response = await fetch('/.netlify/functions/shopify-get-customers', adminFetchInit({ method: 'GET' }));
                      const data = await response.json();
                      console.log('📋 Raw API Response:', data);
                      showToastMessage(`API Test: ${data.success ? 'Success' : 'Failed'} - Check console for details`, data.success ? 'info' : 'error');
                    } catch (error) {
                      console.error('🚨 Direct API test failed:', error);
                      showToastMessage(`Direct API test failed: ${error.message}`, 'error');
                    }
                  }}
                  className="px-4 py-3 bg-blue-700 hover:bg-blue-600 border border-blue-600 rounded-lg text-blue-300 hover:text-white transition-colors"
                  title="Test API directly"
                >
                  🔍
                </button>
              </div>

              <div className="mt-4 text-sm text-gray-400">
                Showing {filteredCustomers.length} of {customers.length} customers
              </div>
            </div>

            {/* Customers Table */}
            {loadingCustomers ? (
              <div className="bg-gray-800 rounded-2xl border border-gray-700 p-16 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
                <h3 className="text-lg font-medium text-white mb-2">Loading Customers...</h3>
                <p className="text-gray-400">Please wait while we fetch your customers</p>
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="bg-gray-800 rounded-2xl border border-gray-700 p-16 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-pink-900/20 to-purple-900/20 rounded-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {customers.length === 0 ? 'No real customers fetched from Shopify' : 'No matching customers'}
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed">
                    {customers.length === 0 
                      ? 'Either your Shopify store has no customers yet, or there may be an API configuration issue. Check the console for detailed error messages.'
                      : 'Try adjusting your search terms to find what you\'re looking for.'
                    }
                  </p>
                  {customers.length === 0 && (
                    <div className="text-sm text-gray-500 bg-gray-800 border border-gray-600 rounded-lg p-4">
                      <p className="font-medium mb-2">🔧 Debugging Tips:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Check browser console for specific API errors</li>
                        <li>Verify SHOPIFY_SHOP_NAME and SHOPIFY_ACCESS_TOKEN environment variables</li>
                        <li>Ensure your Shopify store has customers in it</li>
                        <li>Check Netlify function logs for detailed error information</li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
                {/* Table Header */}
                <div className="px-6 py-4 border-b border-gray-700">
                  <h3 className="text-lg font-medium text-white">Customers ({filteredCustomers.length})</h3>
                  <p className="text-gray-400 text-sm">Manage and track your customer relationships</p>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Customer name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email subscription</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Location</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Orders</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Amount spent</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {filteredCustomers.map((customer) => (
                        <tr key={customer.id} className="hover:bg-gray-700/50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-gradient-to-br from-pink-600 to-purple-600 rounded-full flex items-center justify-center mr-3">
                                <span className="text-white font-semibold text-sm">
                                  {(() => {
                                    const firstName = customer.firstName || '';
                                    const lastName = customer.lastName || '';
                                    const email = customer.email || '';
                                    
                                    // Try to get initials from first/last name
                                    const firstInitial = firstName.charAt(0).toUpperCase();
                                    const lastInitial = lastName.charAt(0).toUpperCase();
                                    
                                    if (firstInitial && lastInitial) {
                                      return firstInitial + lastInitial;
                                    } else if (firstInitial) {
                                      return firstInitial;
                                    } else if (lastInitial) {
                                      return lastInitial;
                                    } else if (email) {
                                      return email.charAt(0).toUpperCase();
                                    } else {
                                      return 'U';
                                    }
                                  })()}
                                </span>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-white">
                                  {(() => {
                                    const firstName = customer.firstName || '';
                                    const lastName = customer.lastName || '';
                                    const email = customer.email || '';
                                    
                                    // Build display name
                                    if (firstName && lastName) {
                                      return `${firstName} ${lastName}`;
                                    } else if (firstName) {
                                      return firstName;
                                    } else if (lastName) {
                                      return lastName;
                                    } else if (email) {
                                      return email.split('@')[0];
                                    } else {
                                      return 'Unnamed Customer';
                                    }
                                  })()}
                                </div>
                                <div className="text-sm text-gray-400">{customer.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              customer.emailSubscription === 'Subscribed' || customer.acceptsMarketing
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {customer.emailSubscription || (customer.acceptsMarketing ? 'Subscribed' : 'Not subscribed')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-white">{customer.location || 'Not provided'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-white">
                              {customer.ordersCount || 0} {customer.ordersCount === 1 ? 'order' : 'orders'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-white">{customer.totalSpent || '€0.00'}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Customer Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden border border-gray-700">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-pink-600 to-purple-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">New Customer</h2>
                    <p className="text-pink-100 text-sm">Add a new customer to your store</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowCreateModal(false)}
                  className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 max-h-[75vh] overflow-y-auto sidebar-scroll">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Customer Overview */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-gray-700 rounded-xl p-6 border border-gray-600">
                    <h3 className="text-lg font-semibold text-white mb-4">Customer overview</h3>
                    
                    {/* Name Fields */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">First name</label>
                        <input
                          type="text"
                          value={customerForm.firstName}
                          onChange={(e) => handleFormChange('firstName', e.target.value)}
                          className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-400"
                          placeholder="Enter first name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Last name</label>
                        <input
                          type="text"
                          value={customerForm.lastName}
                          onChange={(e) => handleFormChange('lastName', e.target.value)}
                          className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-400"
                          placeholder="Enter last name"
                        />
                      </div>
                    </div>



                    {/* Email */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                      <input
                        type="email"
                        value={customerForm.email}
                        onChange={(e) => handleFormChange('email', e.target.value)}
                        className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-400"
                        placeholder="Enter email address"
                      />
                    </div>

                    {/* Phone Number */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Phone number</label>
                      <div className="w-full flex">
                        <div className="relative flex items-center">
                          {/* Flag Icon */}
                          <div className="absolute left-3 z-10 pointer-events-none">
                            {(() => {
                              const selectedCountry = countryCodes.find(c => c.code === customerForm.countryCode);
                              const flagClass = selectedCountry ? getFlagClass(selectedCountry.countryCode) : null;
                              return flagClass ? (
                                <span 
                                  className={`${flagClass} text-sm`}
                                  style={{
                                    fontSize: '16px',
                                    width: '20px',
                                    height: '15px',
                                    borderRadius: '2px',
                                    display: 'inline-block'
                                  }}
                                />
                              ) : (
                                <span className="text-gray-400 text-xs">🌍</span>
                              );
                            })()}
                          </div>
                          <select
                            value={customerForm.countryCode}
                            onChange={(e) => handleCountryCodeChange(e.target.value)}
                            className="border-r-[0] appearance-none bg-gray-800 border border-gray-600 rounded-l-lg pl-10 pr-8 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 w-[180px] focus:z-10"
                          >
                            <option value="" className="bg-gray-800 text-white">Select country</option>
                            {countryCodes.map((country) => (
                              <option key={country.code + country.country} value={country.code} className="bg-gray-800 text-white">
                                {country.code} - {country.country}
                              </option>
                            ))}
                          </select>
                          <svg className="w-4 h-4 absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                        <input
                          type="tel"
                          value={customerForm.phone}
                          onChange={(e) => handleFormChange('phone', e.target.value)}
                          className="border-l-[0] flex-1 bg-gray-800 border border-l-0 border-gray-600 rounded-r-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-400 focus:z-10"
                          placeholder="123456789"
                        />
                      </div>
                    </div>

                    {/* Marketing Consent (aligned with Shopify API) */}
                    <div className="space-y-3 mb-4">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={customerForm.emailMarketingConsent === 'subscribed'}
                          onChange={(e) => handleFormChange('emailMarketingConsent', e.target.checked ? 'subscribed' : 'not_subscribed')}
                          className="w-4 h-4 text-pink-600 bg-gray-700 border-gray-600 rounded focus:ring-pink-500 focus:ring-2"
                        />
                        <span className="text-sm text-gray-300">Customer agreed to receive marketing emails.</span>
                      </label>

                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={customerForm.smsMarketingConsent === 'subscribed'}
                          onChange={(e) => handleFormChange('smsMarketingConsent', e.target.checked ? 'subscribed' : 'not_subscribed')}
                          className="w-4 h-4 text-pink-600 bg-gray-700 border-gray-600 rounded focus:ring-pink-500 focus:ring-2"
                        />
                        <span className="text-sm text-gray-300">Customer agreed to receive SMS marketing text messages.</span>
                      </label>
                    </div>

                    <p className="text-sm text-gray-300 bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-3">
                      You should ask your customers for permission before you subscribe them to your marketing emails or SMS.
                    </p>
                  </div>

                  {/* Default Address */}
                  <div className="bg-gray-700 rounded-xl p-6 border border-gray-600">
                    <h3 className="text-lg font-semibold text-white mb-4">Default address</h3>
                    <p className="text-gray-300 text-sm mb-2">The primary address of this customer</p>
                    <div className="text-xs text-blue-400 bg-blue-900/20 border border-blue-600/30 rounded-lg p-2 mb-4 flex items-center space-x-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Country is automatically set based on your phone number selection above</span>
                    </div>
                    
                    <div className="space-y-4">
                      {/* First and Last Name for Address */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">First name</label>
                          <input
                            type="text"
                            value={customerForm.address.firstName}
                            onChange={(e) => handleFormChange('address.firstName', e.target.value)}
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-400"
                            placeholder="First name (for address)"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Last name</label>
                          <input
                            type="text"
                            value={customerForm.address.lastName}
                            onChange={(e) => handleFormChange('address.lastName', e.target.value)}
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-400"
                            placeholder="Last name (for address)"
                          />
                        </div>
                      </div>

                      {/* Company */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Company</label>
                        <input
                          type="text"
                          value={customerForm.address.company}
                          onChange={(e) => handleFormChange('address.company', e.target.value)}
                          className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-400"
                          placeholder="Company name (optional)"
                        />
                      </div>

                      {/* Address */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Address</label>
                        <input
                          type="text"
                          value={customerForm.address.address1}
                          onChange={(e) => handleFormChange('address.address1', e.target.value)}
                          className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-400"
                          placeholder="Street address"
                        />
                      </div>

                      {/* Apartment, suite, etc */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Apartment, suite, etc.</label>
                        <input
                          type="text"
                          value={customerForm.address.address2}
                          onChange={(e) => handleFormChange('address.address2', e.target.value)}
                          className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-400"
                          placeholder="Unit, floor, etc. (optional)"
                        />
                      </div>

                      {/* Postal code and City */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Postal code</label>
                          <input
                            type="text"
                            value={customerForm.address.zip}
                            onChange={(e) => handleFormChange('address.zip', e.target.value)}
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-400"
                            placeholder="Postal code"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">City</label>
                          <input
                            type="text"
                            value={customerForm.address.city}
                            onChange={(e) => handleFormChange('address.city', e.target.value)}
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-400"
                            placeholder="City"
                          />
                        </div>
                      </div>

                      {/* Province/State */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          {customerForm.address.countryCode === 'US' ? 'State' : 
                           customerForm.address.countryCode === 'CA' ? 'Province' : 
                           customerForm.address.countryCode === 'PH' ? 'Province' :
                           'Province/State'}
                        </label>
                        <input
                          type="text"
                          value={customerForm.address.province}
                          onChange={(e) => handleFormChange('address.province', e.target.value)}
                          className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-400"
                          placeholder={
                            customerForm.address.countryCode === 'US' ? 'State (e.g., CA, NY, TX)' : 
                            customerForm.address.countryCode === 'CA' ? 'Province (e.g., ON, BC, QC)' : 
                            customerForm.address.countryCode === 'PH' ? 'Province (e.g., Misamis Oriental, Cebu, Manila)' :
                            'Province or State'
                          }
                        />
                      </div>

                      {/* Phone for Address (optional - can be different from main phone) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Phone (for this address)</label>
                        <input
                          type="tel"
                          value={customerForm.address.phone}
                          onChange={(e) => handleFormChange('address.phone', e.target.value)}
                          className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-400"
                          placeholder="Phone number for this address (optional)"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Tax Details */}
                  <div className="bg-gray-700 rounded-xl p-6 border border-gray-600">
                    <h3 className="text-lg font-semibold text-white mb-4">Tax details</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">VAT number</label>
                        <input
                          type="text"
                          value={customerForm.vatNumber}
                          onChange={(e) => handleFormChange('vatNumber', e.target.value)}
                          className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-400"
                          placeholder="Enter VAT number"
                        />
                        <p className="text-xs text-gray-400 mt-1">Valid VAT numbers apply the reverse charge exemption</p>
                      </div>
                      
                      <div>
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={customerForm.taxExempt}
                            onChange={(e) => handleFormChange('taxExempt', e.target.checked)}
                            className="w-4 h-4 text-pink-600 bg-gray-700 border-gray-600 rounded focus:ring-pink-500 focus:ring-2"
                          />
                          <span className="text-sm text-gray-300">Customer is tax exempt</span>
                        </label>
                        <p className="text-xs text-gray-400 mt-2">Check this if the customer should not be charged taxes</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Notes & Tags */}
                <div className="space-y-6">
                  {/* Notes */}
                  <div className="bg-gray-700 rounded-xl p-6 border border-gray-600">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white">Notes</h3>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                    <textarea
                      value={customerForm.note}
                      onChange={(e) => handleFormChange('note', e.target.value)}
                      className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 resize-none placeholder-gray-400"
                      rows="4"
                      placeholder="Notes are private and won't be shared with the customer."
                    />
                  </div>

                  {/* Tags */}
                  <div className="bg-gray-700 rounded-xl p-6 border border-gray-600">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white">Tags</h3>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={customerForm.tags}
                      onChange={(e) => handleFormChange('tags', e.target.value)}
                      className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-400"
                      placeholder="Add tags separated by commas"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-700 px-6 py-4 border-t border-gray-600">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-600 rounded-lg text-gray-300 bg-gray-800 hover:bg-gray-700 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCustomer}
                  className="px-6 py-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg hover:from-pink-700 hover:to-purple-700 transition-colors"
                >
                  Save Customer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className={`fixed bottom-4 right-4 z-[10000] transform transition-all duration-500 ease-in-out ${
          showToast ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}>
          <div className={`flex items-center p-4 rounded-lg shadow-lg min-w-[300px] max-w-md ${
            toastType === 'success' 
              ? 'bg-green-600 text-white' 
              : toastType === 'info'
              ? 'bg-blue-600 text-white'
              : 'bg-red-600 text-white'
          }`}>
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {toastType === 'success' ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : toastType === 'info' ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium">{toastMessage}</p>
              </div>
              <button 
                onClick={() => setShowToast(false)}
                className="flex-shrink-0 text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customer; 